import { QrContentKey } from 'src/app/business/qr-create/qr-create-bean';

export interface DeepLink {
  scheme: string;
  path: string;
  params: URLSearchParams;
}

export enum DeepLinkOpenKind {
  QR_CODE = 'qr-code',
  ID_SCAN = 'id-scan',
  INVALID = 'invalid',
}

/** Classified deep-link open intent. Navigation stays in AppService. */
export type DeepLinkOpenAction =
  | { kind: DeepLinkOpenKind.QR_CODE; key: QrContentKey }
  | { kind: DeepLinkOpenKind.ID_SCAN; token: string }
  | { kind: DeepLinkOpenKind.INVALID };

/**
 * Parses / classifies app deep links (open path).
 * QR create encode string comes from server (`QRContentKey.content`).
 * Shape: {scheme}://id-scan?token= | {scheme}://qr-code?data={urlencoded key JSON}
 */
export class DeepLinkUtility {

  public static readonly ID_SCAN_PATH: string = 'id-scan';
  public static readonly QR_CODE_PATH: string = 'qr-code';

  private static readonly DEEP_LINK_PATTERN: RegExp = /^([a-zA-Z][a-zA-Z0-9+.\-]*):\/\/([^\/?#]+)\/?(?:\?([^#]*))?/;

  public static parse(url: string): DeepLink | null {
    let match = DeepLinkUtility.DEEP_LINK_PATTERN.exec((url || '').trim());
    if (!match) return null;
    return { scheme: match[1], path: match[2].toLowerCase(), params: new URLSearchParams(match[3] || '') };
  }

  public static isQrCodeLink(link: DeepLink | null): boolean {
    return link?.path === DeepLinkUtility.QR_CODE_PATH;
  }

  public static isIdScanLink(link: DeepLink | null): boolean {
    return link?.path === DeepLinkUtility.ID_SCAN_PATH;
  }

  /** Session token from an id-scan deep link. Null when path/token are missing. */
  public static getIdScanToken(link: DeepLink | null): string | null {
    if (!DeepLinkUtility.isIdScanLink(link)) return null;
    let token = link!.params.get('token') || '';
    return token || null;
  }

  /**
   * Raw key JSON from qr-code?data= (same string server built via toJson). Null if missing/invalid.
   * Prefer this for retrieve POST body without field remapping.
   */
  public static getQrContentKeyJson(link: DeepLink | null): string | null {
    if (!DeepLinkUtility.isQrCodeLink(link)) return null;
    let data = link!.params.get('data') || '';
    if (!data) return null;
    try {
      let key = JSON.parse(data) as QrContentKey;
      if (!key?.id || !key?.monthYear || !key?.day) return null;
      return data;
    } catch {
      return null;
    }
  }

  /**
   * Map a qr-code deep link to the qrContentRetrieve request key (JSON.parse of data=).
   */
  public static toQrContentKey(link: DeepLink | null): QrContentKey | null {
    let raw = DeepLinkUtility.getQrContentKeyJson(link);
    if (!raw) return null;
    let key = JSON.parse(raw) as QrContentKey;
    if (key.qrType) {
      key.qrType = key.qrType.toUpperCase();
    }
    return key;
  }

  public static parseQrContentKey(url: string): QrContentKey | null {
    return DeepLinkUtility.toQrContentKey(DeepLinkUtility.parse(url));
  }

  /**
   * Classify an open URL into a typed action (no scheme check - OS already routed).
   */
  public static resolveOpen(url: string): DeepLinkOpenAction {
    let link = DeepLinkUtility.parse(url);
    if (!link) {
      return { kind: DeepLinkOpenKind.INVALID };
    }

    if (DeepLinkUtility.isQrCodeLink(link)) {
      let key = DeepLinkUtility.toQrContentKey(link);
      return key ? { kind: DeepLinkOpenKind.QR_CODE, key } : { kind: DeepLinkOpenKind.INVALID };
    }

    if (DeepLinkUtility.isIdScanLink(link)) {
      let token = DeepLinkUtility.getIdScanToken(link);
      return token ? { kind: DeepLinkOpenKind.ID_SCAN, token } : { kind: DeepLinkOpenKind.INVALID };
    }

    return { kind: DeepLinkOpenKind.INVALID };
  }
}
